import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Modal from 'react-bootstrap/Modal';
import Table from 'react-bootstrap/Table';
import { Button } from 'bootstrap-4-react/lib/components';
import { formatPhoneNumberIntl } from 'react-phone-number-input';
import { toast } from 'react-toastify';
import {
    getSellersforMultipleCompany,
    getAllAssociatedUsers,
} from '../../store/actions';
import { startImpersonation } from '../../store/actions/impersonation.action';
import AddCompanyModal from '../modal/AddCompanyModal';
import AddUserModal from '../modal/AddUserModal';
import AlertDialogModal from '../common/AlertDialog';
import eye_icon from '../../assets/eye_icon.svg';
import edit_icon from '../../assets/edit_icon.svg';
import { checkPermission } from '../../config/utils';
import WalletBalanceCell from './WalletBalanceCell';
import { getCompanyFundRows } from '../../store/actions/fundManagement';
import { companyListBackDetails } from '../../store/actions/rateCard';
import {
    accountsManagement,
    featureSubmodule,
    featureSubmodulePermission,
    permssionCreate,
    settings,
    SUBF_FUNDMANAGEMENT,
    SUBF_IMPERSONATEUSER,
    SUBF_SELLER,
    SUBF_USER,
} from '../../constant';
import './companyDetailsPopup.css';
import '../modal/AddCompanyModal/topUpModal.css';

const openWhatsApp = (phoneNumber) => {
    const digits = (phoneNumber || '').replaceAll(/\D/g, '');
    if (!digits) return;
    window.open(`https://wa.me/${digits}`, '_blank');
};

const renderWhatsAppPhone = (phoneNumber) => (
    <span className="phn-value">
        {phoneNumber && (
            <button
                type="button"
                className="sortBtn whatsappIcon inline-edit mr-1"
                onClick={() => openWhatsApp(phoneNumber)}
                aria-label="Open WhatsApp"
            >
                <i className="fa fa-whatsapp" aria-hidden="true"></i>
            </button>
        )}
        {formatPhoneNumberIntl(phoneNumber) || phoneNumber}
    </span>
);

const addSellerIds = (target, sellerIds) => {
    (sellerIds || []).forEach((sellerId) => {
        if (!target.includes(sellerId)) target.push(sellerId);
    });
};

// The users API returns one row per seller; collapse to one row per user with all seller ids merged.
export const mergeUsersById = (rows) => {
    const byId = new Map();
    (rows || []).forEach((row) => {
        const existing = byId.get(row?.id);
        if (existing) {
            addSellerIds(existing.UserSellerIdList, row?.UserSellerIdList);
            return;
        }
        byId.set(row?.id, { ...row, UserSellerIdList: [...(row?.UserSellerIdList || [])] });
    });
    return Array.from(byId.values());
};

const CompanyDetailsPopup = (props) => {
    const { open, onClose, company, initialTab, employeePermissions, filterData } = props;
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(initialTab || 'sellers');
    const [addSellerShow, setAddSellerShow] = useState(false);
    const [addUserShow, setAddUserShow] = useState(false);
    const [sellersLoading, setSellersLoading] = useState(false);
    const [sellersLoaded, setSellersLoaded] = useState(false);
    const [impersonateTarget, setImpersonateTarget] = useState(null);
    const [impersonateLoading, setImpersonateLoading] = useState(false);
    const [walletLoadingId, setWalletLoadingId] = useState(null);

    const sellersData = useSelector((state) => state?.company?.getMultipleCompanySellers);
    const usersData = useSelector((state) => state?.company?.getAllUsersData);
    const usersStatus = useSelector((state) => state?.company?.getAllUsersDataStatus);
    const currentUser = useSelector((state) => state?.auth?.user);
    const isSuperAdmin = !!currentUser?.Value?.isSuperAdmin;

    const companyId = company?.Id;
    const companyName = company?.name || '';

    useEffect(() => {
        if (open) {
            setActiveTab(initialTab || 'sellers');
        }
    }, [open, initialTab]);

    useEffect(() => {
        if (!open || !companyId) return;
        if (activeTab === 'sellers') {
            setSellersLoading(true);
            setSellersLoaded(false);
            Promise.resolve(dispatch(getSellersforMultipleCompany([companyId])))
                .finally(() => {
                    setSellersLoading(false);
                    setSellersLoaded(true);
                });
        } else if (activeTab === 'users') {
            dispatch(
                getAllAssociatedUsers(
                    () => { },
                    500,
                    1,
                    { sortColumn: '', sortOrder: 'ASCENDING' },
                    companyId,
                    0
                )
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, companyId, activeTab]);

    const handleSellerWalletClick = async (seller) => {
        if (walletLoadingId) return;
        setWalletLoadingId(seller?.id);
        try {
            const fundRows = await getCompanyFundRows(company, currentUser);
            const fundRow = fundRows.find((row) => row?.sellerId === seller?.id);
            if (!fundRow) {
                toast.error('No wallet found for this seller');
                return;
            }
            dispatch(companyListBackDetails(filterData));
            onClose();
            navigate(`/fundManagement/history/fd-${fundRow.creditCompanySellerId}-3pl`, {
                state: { rowData: fundRow, returnTo: '/companyManagement' },
            });
        } catch (e) {
            toast.error(e?.response?.data?.errorDescription || 'Unable to load wallet details');
        } finally {
            setWalletLoadingId(null);
        }
    };

    const handleViewSeller = (rowData) => {
        onClose();
        navigate(`/editSeller/sell-${rowData?.id}`, { state: { data: rowData, editable: true } });
    };

    const handleEditSeller = (rowData) => {
        onClose();
        navigate(`/editSeller/sell-${rowData?.id}`, { state: { data: rowData, editable: false } });
    };

    const handleViewUser = (rowData) => {
        onClose();
        navigate(`/editUser/user-${rowData?.id}`, { state: { userdata: rowData, editable: true } });
    };

    const handleEditUser = (rowData) => {
        onClose();
        navigate(`/editUser/user-${rowData?.id}`, { state: { userdata: rowData, editable: false } });
    };

    const refreshSellers = () => {
        if (!companyId) return;
        setSellersLoading(true);
        setSellersLoaded(false);
        Promise.resolve(dispatch(getSellersforMultipleCompany([companyId])))
            .finally(() => {
                setSellersLoading(false);
                setSellersLoaded(true);
            });
    };

    const refreshUsers = () => {
        if (!companyId) return;
        dispatch(
            getAllAssociatedUsers(
                () => { },
                500,
                1,
                { sortColumn: '', sortOrder: 'ASCENDING' },
                companyId,
                0
            )
        );
    };

    const canAddSeller = checkPermission(accountsManagement, SUBF_SELLER, permssionCreate, featureSubmodulePermission, employeePermissions);
    const canAddUser = checkPermission(accountsManagement, SUBF_USER, permssionCreate, featureSubmodulePermission, employeePermissions);
    const canImpersonate = isSuperAdmin && checkPermission(accountsManagement, SUBF_IMPERSONATEUSER, '', featureSubmodule, employeePermissions);
    const canViewFunds = checkPermission(settings, SUBF_FUNDMANAGEMENT, null, featureSubmodule, employeePermissions);

    const openImpersonateConfirm = (rowData) => {
        setImpersonateTarget(rowData);
    };

    const closeImpersonateConfirm = () => {
        if (impersonateLoading) return;
        setImpersonateTarget(null);
    };

    const confirmImpersonate = () => {
        if (!impersonateTarget) return;
        setImpersonateLoading(true);
        Promise.resolve(
            dispatch(
                startImpersonation({
                    targetUserId: impersonateTarget?.id,
                    originContext: { companyId },
                })
            )
        )
            .then(() => {
                toast.success('Impersonation started');
                setImpersonateTarget(null);
                onClose();
                navigate('/');
            })
            .catch((err) => {
                toast.error(err?.response?.data?.message?.appStatusDescription
                    || err?.response?.data?.message
                    || err?.message
                    || 'Failed to start impersonation');
            })
            .finally(() => {
                setImpersonateLoading(false);
            });
    };

    const sellerRows = sellersData?.Entity?.listSellerMasterOrdersEntity || [];
    // The API returns one user row per seller; show each user once with all seller ids merged.
    const userRows = useMemo(
        () => mergeUsersById(usersData?.Entity?.listUserOrdersEntity),
        [usersData]
    );

    const renderSellersTab = () => (
        <div className="company-popup-section">
            <div className="company-popup-section-head">
                <h6 className="mb-0 company-popup-section-title">Associated Sellers</h6>
                {canAddSeller && (
                    <Button
                        className="cus-seconday-bg-btn cusbtn"
                        onClick={() => setAddSellerShow(true)}
                    >
                        + Add Seller
                    </Button>
                )}
            </div>
            <div className="tableFixHead incomingTbl company-popup-table-wrapper">
                <Table className="table table-height cus-left-right-col-layout">
                    <thead>
                        <tr>
                            <th>Seller Brand Name</th>
                            <th>Country</th>
                            <th>State</th>
                            <th>Phone Number</th>
                            <th>Wallet Balance</th>
                            <th className="sticky"><span className="action-text">Action</span></th>
                        </tr>
                    </thead>
                    <tbody>
                        {sellerRows.map((item) => (
                            <tr key={item?.id}>
                                <td>{item?.newSellerName}</td>
                                <td>{item?.defaultCountry}</td>
                                <td>{item?.defaultState}</td>
                                <td>{renderWhatsAppPhone(item?.phoneNumber)}</td>
                                <td>
                                    <WalletBalanceCell
                                        balance={item?.walletBalance ?? item?.WalletBalance}
                                        canView={canViewFunds}
                                        loading={walletLoadingId === item?.id}
                                        onClick={() => handleSellerWalletClick(item)}
                                    />
                                </td>
                                <td className="sticky">
                                    <button type="button" className="sortBtn" onClick={() => handleViewSeller(item)} aria-label="View seller">
                                        <img className="editIcon mr-15" src={eye_icon} alt="" />
                                    </button>
                                    <button type="button" className="sortBtn" onClick={() => handleEditSeller(item)} aria-label="Edit seller">
                                        <img className="editIcon mr-15" src={edit_icon} alt="" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {sellersLoading && sellerRows.length === 0 && (
                            <tr><td colSpan="6" align="center">Loading...</td></tr>
                        )}
                        {!sellersLoading && sellersLoaded && sellerRows.length === 0 && (
                            <tr><td colSpan="6" align="center">No Record Found</td></tr>
                        )}
                    </tbody>
                </Table>
            </div>
            {addSellerShow && (
                <AddCompanyModal
                    type="seller"
                    associatedSeller={true}
                    companyId={companyId}
                    show={addSellerShow}
                    onHide={() => { setAddSellerShow(false); refreshSellers(); }}
                />
            )}
        </div>
    );

    const renderUsersTab = () => (
        <div className="company-popup-section">
            <div className="company-popup-section-head">
                <h6 className="mb-0 company-popup-section-title">Associated Users</h6>
                {canAddUser && (
                    <Button
                        className="cus-seconday-bg-btn cusbtn"
                        onClick={() => setAddUserShow(true)}
                    >
                        + Add User
                    </Button>
                )}
            </div>
            <div className="tableFixHead incomingTbl company-popup-table-wrapper">
                <Table className="table table-height cus-left-right-col-layout">
                    <thead>
                        <tr>
                            <th>User Name</th>
                            <th>Email Address</th>
                            <th>Phone Number</th>
                            <th className="sticky"><span className="action-text">Action</span></th>
                        </tr>
                    </thead>
                    <tbody>
                        {userRows.map((u) => (
                            <tr key={u?.id}>
                                <td>{u?.newUserName}</td>
                                <td>{u?.userEmailId}</td>
                                <td>{renderWhatsAppPhone(u?.phoneNumber)}</td>
                                <td className="sticky">
                                    <button type="button" className="sortBtn" onClick={() => handleViewUser(u)} aria-label="View user">
                                        <img className="editIcon mr-15" src={eye_icon} alt="" />
                                    </button>
                                    <button type="button" className="sortBtn" onClick={() => handleEditUser(u)} aria-label="Edit user">
                                        <img className="editIcon mr-15" src={edit_icon} alt="" />
                                    </button>
                                    {canImpersonate && (
                                        <button
                                            type="button"
                                            className="sortBtn"
                                            onClick={() => openImpersonateConfirm(u)}
                                            aria-label="Impersonate as this user"
                                            title="Impersonate as this user"
                                        >
                                            <i className="fa fa-user-secret mr-15" aria-hidden="true" />
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {usersStatus === 'PENDING' && userRows.length === 0 && (
                            <tr><td colSpan="4" align="center">Loading...</td></tr>
                        )}
                        {usersStatus === 'SUCCESS' && userRows.length === 0 && (
                            <tr><td colSpan="4" align="center">No Record Found</td></tr>
                        )}
                    </tbody>
                </Table>
            </div>
            {addUserShow && (
                <AddUserModal
                    show={addUserShow}
                    setTotalPages={() => { }}
                    onHide={() => { setAddUserShow(false); refreshUsers(); }}
                    handleUserView={() => { }}
                    filterValue={{ pageIndex: 1, pageSize: 30, sortColumn: '', sortOrder: 'ASCENDING' }}
                    currentPage={1}
                    numberOfPage={30}
                    companyId={companyId}
                    sellerId={0}
                    type="associatedUser"
                />
            )}
        </div>
    );

    return (
        <Modal
            show={!!open}
            onHide={onClose}
            size="lg"
            centered
            backdrop={true}
            className="topUpModal companyDetailsPopup"
        >
            <div className="top_modal_header_container">
                <span>{companyName || 'Company Details'}</span>
                <div className="modalDeleteWrapper">
                    <button
                        type="button"
                        onClick={onClose}
                        className="btn-close"
                        aria-label="Close"
                    ></button>
                </div>
            </div>
            <div className="modal_container_rate_card fontFix companyDetailsPopup-body">
                <ul className="nav nav-tabs company-popup-tabs mb-3">
                    <li className="nav-item">
                        <button
                            type="button"
                            className={`nav-link ${activeTab === 'sellers' ? 'active' : ''}`}
                            onClick={() => setActiveTab('sellers')}
                        >
                            Associated Sellers
                        </button>
                    </li>
                    <li className="nav-item">
                        <button
                            type="button"
                            className={`nav-link ${activeTab === 'users' ? 'active' : ''}`}
                            onClick={() => setActiveTab('users')}
                        >
                            Associated Users
                        </button>
                    </li>
                </ul>
                <div className="company-popup-tab-body" key={activeTab}>
                    {activeTab === 'sellers' ? renderSellersTab() : renderUsersTab()}
                </div>
            </div>
            {impersonateTarget && (
                <AlertDialogModal
                    modalShow={!!impersonateTarget}
                    headingText="Impersonate User"
                    messageText={`You are about to impersonate ${impersonateTarget?.newUserName || impersonateTarget?.userEmailId || 'this user'}. You will have read-only access to their account. Continue?`}
                    handleYesDeleteAlert={confirmImpersonate}
                    handleCancelDeleteAlert={closeImpersonateConfirm}
                    loader={impersonateLoading}
                    zIndex={1400}
                />
            )}
        </Modal>
    );
};

CompanyDetailsPopup.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    company: PropTypes.object,
    initialTab: PropTypes.oneOf(['sellers', 'users']),
    employeePermissions: PropTypes.array,
    filterData: PropTypes.object,
};

export default CompanyDetailsPopup;
